import { sameSeatNumber } from 'utils/seatPricing';

/**
 * Resolve the presentation props for a seat pricing detail row, shared by the
 * zone-based pricing tenant and the Privileged Seat (per-seat rgb_color)
 * flow. Pure: callers pass already-bound `t` and `getCategoryTranslation`.
 *
 * - Privileged Seat: indicator color comes from the matched seat's
 *   `rgb_color` (formatted as a CSS `rgb()` string) and the label is the
 *   passenger category translation. The zone map is never consulted.
 * - Zone tenant: indicator color and label come from the resolved `seatZone`.
 *
 * Price always comes from `ticketData.total` — the backend-authoritative final
 * amount that already accounts for any discounts (not `amount + taxes` which
 * is the pre-discount subtotal).
 *
 * @param {object} params - isPrivilegedSeat, ticketData, selectedSeatsForTrip, seatZone, t, getCategoryTranslation.
 * @param {boolean} params.isPrivilegedSeat - True when seats carry rgb_color.
 * @param {object} params.ticketData - Ticket row data (seat, category, showSeat, seatHasNumber, isAdjacentSeat, total).
 * @param {Array<object>} [params.selectedSeatsForTrip] - Selected seats for the leg.
 * @param {{color: string|null, label: string, zoneKey: string}|null} [params.seatZone] - Resolved zone (zone tenant only).
 * @param {Function} params.t - i18n translation function.
 * @param {Function} params.getCategoryTranslation - Maps (isAdjacentSeat, category) to a translated label.
 * @returns {{seatLabel: string, indicatorColor: (string|undefined), label: (string|undefined), price: number}}
 */
const resolveSeatPricingRowPresentation = ({
  isPrivilegedSeat,
  ticketData = {},
  selectedSeatsForTrip = [],
  seatZone = null,
  t,
  getCategoryTranslation,
}) => {
  const seatLabel =
    ticketData.showSeat && ticketData.seatHasNumber
      ? `${t('general:seat')} ${ticketData.seat}`
      : t('text.no_assigned_seat');

  if (isPrivilegedSeat) {
    const matchedSeat = (selectedSeatsForTrip || []).find((seat) => {
      return sameSeatNumber(seat?.number, ticketData.seat);
    });
    const rgb = matchedSeat?.rgbColor ?? ticketData?.rgbColor;

    return {
      seatLabel,
      indicatorColor: rgb ? `rgb(${rgb})` : undefined,
      label: getCategoryTranslation(ticketData.isAdjacentSeat, ticketData.category),
      price: ticketData.total,
    };
  }

  return {
    seatLabel,
    indicatorColor: seatZone?.color || undefined,
    label: seatZone?.label || seatZone?.zoneKey,
    price: ticketData.total,
  };
};

export default resolveSeatPricingRowPresentation;
